import type { BlogPost } from "@/data/blogPosts";

export const RELATED_AI_INSIGHTS_CAROUSEL_SLUGS = new Set([
  "aeo-benchmark-authority-oligopoly-ai-search-visibility-2026",
  "agent-advertising-attribution-wall-agentic-ads-measurement-2026",
  "stripe-openrouter-8-billion-ai-model-routing-economics-2026",
]);

type RelatedArticleCarouselContext = {
  heading: string;
  description: string;
  itemListName: string;
  cardLabel: string;
  previousLabel: string;
  nextLabel: string;
  relevanceSummaries: Readonly<Record<string, string>>;
};

/**
 * Editorial, article-specific context for every live article carousel. The
 * summary is intentionally authored rather than inferred from a title alone,
 * so each card explains its relevance before a reader decides to continue.
 */
export const RELATED_ARTICLE_CAROUSEL_CONTEXTS: Readonly<Record<string, RelatedArticleCarouselContext>> = {
  "us-september-2026-payrolls-ai-finance-jobs": {
    heading: "Related Articles for finance leaders",
    description: "Evidence-led reading on financial-services AI governance, workforce change and accountable measurement.",
    itemListName: "Related financial-services articles",
    cardLabel: "Article",
    previousLabel: "Show previous related financial article",
    nextLabel: "Show next related financial article",
    relevanceSummaries: {
      "dora-ai-compliance-checklist-financial-services-2026": "Connects the payroll context to the operational-resilience and accountability questions financial-services teams face when they introduce AI into consequential workflows.",
      "bank-of-england-agentic-ai-governance-circuit-breakers-financial-stability-2026": "Explains the governance and circuit-breaker questions that sit alongside workforce change when an institution gives agents more authority.",
      "ai-job-displacement-layoffs-restructuring": "Adds the wider workforce lens by separating reported restructuring, automation narratives and causation-the same distinction needed when reading this payroll release.",
      "ai-roi-paradox-b2b-marketing-measurement-2026": "Helps finance leaders assess AI through measured business outcomes and controls rather than tool-adoption headlines or a single jobs report.",
    },
  },
  "aeo-benchmark-authority-oligopoly-ai-search-visibility-2026": {
    heading: "Related authority and evidence analysis",
    description: "Earlier reporting that explains how evidence, measurement and market structure shape AI-search visibility.",
    itemListName: "Related AI-search authority analysis",
    cardLabel: "Analysis",
    previousLabel: "Show previous authority analysis",
    nextLabel: "Show next authority analysis",
    relevanceSummaries: {
      "ai-search-evidence-economy-publisher-scraping-referral-2026": "Tests the evidence economy beneath the authority gap: AI answers need accessible, attributable sources, yet publisher incentives can weaken when referral value falls.",
      "geo-incrementality-attribution-crisis-cmo-measurement-2026": "Separates the visibility pattern in this benchmark from proof of commercial impact, so citation share is not mistaken for revenue causation.",
      "ai-search-shortlist-brand-awareness-recommendation-authority-2026": "Moves from broad authority concentration to the buyer shortlist, showing why familiar brands can still lose recommendation visibility.",
      "nvidia-ai-infrastructure-economics-cmo-pricing-2026": "Adds the capital and infrastructure layer behind AI concentration, which helps explain why visibility and model economics are not purely software questions.",
      "apple-china-ai-model-global-geo-fragmentation-2026": "Shows how country-specific models and partnerships can fragment the authority landscape, limiting any one global GEO playbook.",
      "databricks-enterprise-context-moat-ai-value-layer-2026": "Explains why governed enterprise context can become a durable advantage even when the underlying model layer becomes easier to substitute.",
    },
  },
  "agent-advertising-attribution-wall-agentic-ads-measurement-2026": {
    heading: "Related agentic measurement analysis",
    description: "Further reading on attribution, authority, control and evidence when AI systems influence business decisions.",
    itemListName: "Related agentic measurement analysis",
    cardLabel: "Analysis",
    previousLabel: "Show previous agentic measurement analysis",
    nextLabel: "Show next agentic measurement analysis",
    relevanceSummaries: {
      "geo-incrementality-attribution-crisis-cmo-measurement-2026": "Clarifies what cannot be inferred from an agent impression alone: observed exposure and commercial causation remain different measurement problems.",
      "agent-killability-enterprise-runtime-containment-2026": "Extends the attribution question into operating control by examining whether a business can contain an agent after it acts beyond its intended remit.",
      "ai-singularity-consciousness-capability-controversial-2026": "Frames the issue around capability, autonomy and consequence rather than sentience-the conditions that make agent activity commercially material.",
      "anthropic-multiagent-governance-conflict-enterprise-2026": "Shows how individually authorised agents can still create system-level conflict, a useful warning for any multi-step attribution chain.",
      "prompt-engineering-guide-frameworks-ai-tools-2026": "Provides the practitioner layer: clearer instructions, constraints and guardrails are the starting point for more interpretable agent behaviour.",
      "waymo-robotaxi-20-million-rides-autonomous-driving-safety-2026": "Offers a practical benchmark lens for evaluating autonomous systems against adjusted human outcomes instead of relying on a single surface metric.",
    },
  },
  "stripe-openrouter-8-billion-ai-model-routing-economics-2026": {
    heading: "Related AI economics analysis",
    description: "Context on model routing, enterprise data, infrastructure and governance behind the cost of reliable AI work.",
    itemListName: "Related AI economics analysis",
    cardLabel: "Analysis",
    previousLabel: "Show previous AI economics analysis",
    nextLabel: "Show next AI economics analysis",
    relevanceSummaries: {
      "databricks-enterprise-context-moat-ai-value-layer-2026": "Compares the routing layer with the governed data and context layer that makes a chosen model useful inside a real enterprise workflow.",
      "gemini-37-flash-agentic-price-war-cost-per-workflow-2026": "Turns headline token pricing into the more relevant unit for buyers: the cost of a reliably completed workflow.",
      "ai-singularity-consciousness-capability-controversial-2026": "Connects the commercial routing story to the wider question of how capability and autonomy change the consequences of deploying more powerful systems.",
      "anthropic-multiagent-governance-conflict-enterprise-2026": "Explains why routing and orchestration need governance as well as economics when multiple authorised systems can pull toward different outcomes.",
      "nvidia-ai-infrastructure-economics-cmo-pricing-2026": "Adds the physical infrastructure and capital layer beneath model-routing costs, from compute capacity to power and data-centre demand.",
      "prompt-engineering-guide-frameworks-ai-tools-2026": "Shows the human input layer that determines whether a selected model receives clear goals, constraints and output requirements.",
    },
  },
};

export function shouldUseRelatedAIInsightsCarousel(slug: string) {
  return RELATED_AI_INSIGHTS_CAROUSEL_SLUGS.has(slug);
}

export function getRelatedArticleCarouselContext(slug: string) {
  return RELATED_ARTICLE_CAROUSEL_CONTEXTS[slug];
}

/**
 * Keeps every carousel card explainable if future related-post curation
 * changes before the matching summary map is updated. Current live cards all
 * have an authored summary; this fallback preserves the disclosure contract.
 */
export function resolveRelatedArticleCarouselSummaries(
  currentPost: BlogPost,
  relatedPosts: BlogPost[],
): Record<string, string> {
  const configured = getRelatedArticleCarouselContext(currentPost.slug)?.relevanceSummaries ?? {};
  const currentTopics = currentPost.tags.slice(0, 2).join(" and ") || "the decision in this analysis";

  return Object.fromEntries(
    relatedPosts.map((relatedPost) => [
      relatedPost.slug,
      configured[relatedPost.slug] ??
        `Adds a complementary perspective on ${currentTopics}, helping place this analysis in a wider decision context.`,
    ]),
  );
}

type SelectRelatedInsightsArgs = {
  currentPost: BlogPost;
  directPosts: BlogPost[];
  allPosts: BlogPost[];
  limit?: number;
};

export function selectOlderRelatedInsights({
  currentPost,
  directPosts,
  allPosts,
  limit = 6,
}: SelectRelatedInsightsArgs): BlogPost[] {
  const currentPublishedAt = new Date(currentPost.date).getTime();
  const selected: BlogPost[] = [];
  const seen = new Set([currentPost.slug]);

  const isOlder = (post: BlogPost) => new Date(post.date).getTime() < currentPublishedAt;
  const sharesTopic = (post: BlogPost) =>
    post.tags.some((tag) => currentPost.tags.includes(tag));

  const add = (post: BlogPost) => {
    if (selected.length >= limit || seen.has(post.slug) || !isOlder(post)) return;
    seen.add(post.slug);
    selected.push(post);
  };

  directPosts.forEach(add);

  allPosts
    .filter((post) => !seen.has(post.slug) && isOlder(post) && sharesTopic(post))
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .forEach(add);

  return selected.slice(0, limit);
}
